import { AppBar, Button, Card, IconButton, Tab, Tabs } from "@material-ui/core";
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, Edit } from "@material-ui/icons";
import translations from "../../translations/i18next";
import WarningIcon from '@material-ui/icons/Warning';
import VisibilityIcon from '@material-ui/icons/Visibility';
import * as React from "react";
import * as SettingsActions from '../../redux/settings/settings.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { ModalTypes } from '../../components/Modals/ModalTypes';
import * as CustomerContractDecoder from '../../codec/customerContractInDTODecoder';
import { connect, ConnectedProps } from "react-redux";
import { IState } from "../../redux/store";
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import { getCustomerContractStatusColor } from "../../utils/Utils";
import Permission from "../Permission/Permission";
import ReduxLanguage from "../ReduxLanguage";
import DomainsIcon from '@material-ui/icons/Domain';
import CustomerContractIconCustom from "../CustomerContractIconCustom";
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import CustomerContractForm from "../Forms/CustomerContractForm";
import { ICustomerContractInDTO } from "../../repository/jago/model/input/ICustomerContractInDTO";
import { ICustomerContractFormDTO } from "../../repository/jago/model/input/ICustomerContractFormDTO";
import CustomerContractInfoDetailsView from "../../routes/Credits/CustomerContractInfoDetailsView";
import CustomerContractPlantsSection from "./CustomerContractPlantsSection";
import CustomerContractDocumentsSection from "./CustomerContractDocumentsSection";
import CustomerContractServicesSection from "./CustomerContractServicesSection";
import CustomerContractNotificationsSection from "./CustomerContractNotificationsSection";

const styles = createStyles({
  container: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column',
    paddingBottom: 50,
  },
  headerContainer: {
    marginTop: 20,
    padding: 10,
    borderLeft: '3px solid #0D47A1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  icon: {
    marginRight: 5,
    width: 25,
  },
  title: {
    fontWeight: 'bold'
  },
});

type IReduxProps = ConnectedProps<typeof connector>;

export interface ICustomerContractDetailsViewProps extends WithStyles<typeof styles>{
  onDeleteCustomerContractRequest?: (customerContract: ICustomerContractInDTO) => void;
  initialTab?: number;
}

export type ComponentProps = ICustomerContractDetailsViewProps & IReduxProps;
class CustomerContractDetailsView extends React.Component<ComponentProps, any> {

  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      activeTab: this.props.initialTab || 0,
    }
  }

  async onOpenEditCustomerContractForm() {
    const { dispatch, selectedCustomerContract, selectedCustomer } = this.props;
    if (selectedCustomerContract) {
      const customerContract = selectedCustomerContract as ICustomerContractInDTO
      const customerContractFormData = CustomerContractDecoder.decodeCustomerContractContentToForm(customerContract, [selectedCustomer]);
      dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CREATE_CUSTOMER_CONTRACT_MODAL`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
        modalProps: {
          content: (
            <CustomerContractForm
              customerContract={customerContractFormData}
              customer={selectedCustomer}
              onCreateEditCustomerContract={(newCustomerContractData: ICustomerContractFormDTO) => this.onEditCustomerContractRequest(customerContract.id, newCustomerContractData)}
            />
          ),
          titleMessageKey: 'forms.customerContractFormTitle',
        }
      }));
    }
  }

  public onEditCustomerContractRequest(customerContractId: number, newCustomerContractData: ICustomerContractFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editCustomerContract',
        successMessageKey: 'credits.editCustomerContractConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerContractConfirm(customerContractId, newCustomerContractData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerContractConfirm(customerContractId: number, newCustomerContractData: ICustomerContractFormDTO) {
    const { dispatch, selectedCustomer } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_CONTRACT_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const customerContractUpdated = await dispatch<any>(CreditsActions.editCustomerContract(selectedCustomer.id, customerContractId, newCustomerContractData));
      dispatch<any>(CreditsActions.setSelectedCustomerContract(customerContractUpdated));
      const promises = [
        dispatch<any>(CreditsActions.fetchCustomerContracts({ page: 0, pageSize: 100, customerId: selectedCustomer.id }))
      ]
      if ((window?.location?.pathname || "").startsWith('/contracts')) {
        promises.push(dispatch<any>(CreditsActions.fetchAllCustomerContracts({ page: 0, pageSize: 50 })));
      }
      await Promise.all(promises);
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CREATE_CUSTOMER_CONTRACT_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error)
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_CONTRACT_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.createCustomerContractError',
        }
      }));
    }
  }

  public async onOpenCustomerContractDetails() {
    const { dispatch } = this.props;
    try {
      dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_CONTRACT_DETAILS`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
        modalProps: {
          content: (
            <CustomerContractInfoDetailsView />
          ),
          titleMessageKey: 'credits.customerContractDetails',
        }
      }));
    } catch (error) {

    }
  }

  public render() {
    const { selectedCustomer, selectedCustomerContract, onDeleteCustomerContractRequest, classes} = this.props;
    const { activeTab } = this.state;

    const customerContract = selectedCustomerContract as ICustomerContractInDTO;
    
    return (
      <div className={classes.container}>
        <Card className={classes.headerContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <CustomerContractIconCustom className={classes.icon} style={{}} />
              <div className={classes.headerTextContainer}>
                <div>
                  <h3 className={classes.title}>{customerContract && customerContract.name ? `${customerContract.name}` : ''}</h3>
                  <h5 style={{ color: getCustomerContractStatusColor(customerContract.status || ""), fontWeight: 'bold', whiteSpace: 'normal' }}>{translations.t(`credits.customerContractStatus_${customerContract.status || ""}`).toUpperCase()}</h5>
                </div>
                <IconButton onClick={() => this.onOpenCustomerContractDetails()}>
                  <VisibilityIcon />
                </IconButton>
                <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.CONTRACTS_UPDATE}>
                  <IconButton onClick={() => this.onOpenEditCustomerContractForm()}>
                    <Edit />
                  </IconButton>
                </Permission>
              </div>
            </div>
            <span style={{flexGrow:1}}></span>
            <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.CONTRACTS_DELETE}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'darkred',
                  flexShrink: 0,
                  marginRight: 20,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 10,
                  paddingRight: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {e.stopPropagation(); onDeleteCustomerContractRequest(customerContract)}}
              >
                <DeleteOutline style={{ fontSize: 15 }}/>
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
              </Button>
            </Permission>
            
          </div>
        </Card>
        <AppBar position="sticky" style={{ backgroundColor: 'white', marginTop: 20 }}>
          <Tabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            style={{ backgroundColor: 'white', width: '100%' }}
            onChange={(e, index) => {this.setState({ activeTab: index })}}
          >
            {AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_READ) ? (
              <Tab value={0} icon={<DomainsIcon style={{ fontSize: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="credits.walletPlants" /></h5>} />
            ) : null}
            {AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CONTRACT_SERVICES_READ) ? (
              <Tab value={1} icon={<LocalOfferOutlinedIcon style={{ width: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="credits.contractServices" /></h5>} />
            ) : null}
            <Tab value={2} icon={<InsertDriveFileOutlinedIcon style={{ width: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="credits.contractDocuments" /></h5>} />
            <Tab value={3} icon={<NotificationsActiveOutlinedIcon style={{ width: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="credits.contractNotifications" /></h5>} />
          </Tabs>
        </AppBar>
        {activeTab === 0 ? (
          <CustomerContractPlantsSection
            customer={selectedCustomer}
          />
        ):null}
        {activeTab === 1 ? (
          <CustomerContractServicesSection
            customer={selectedCustomer}
          />
        ):null}
        {activeTab === 2 ? (
          <CustomerContractDocumentsSection
            customer={selectedCustomer}
          />
        ):null}   
        {activeTab === 3 ? (
          <CustomerContractNotificationsSection
            customer={selectedCustomer}
          />
        ):null}        
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    selectedCustomer: state.credits.customers.selectedDetailedCustomer,
    selectedCustomerContract: state.credits.customerContracts.selectedCustomerContract,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles (styles)(CustomerContractDetailsView as any));